import {
  type FunctionDefinition,
  type RouteDefinition,
  DataplaneSpecificConfig,
  ModelsConfigResponse,
  scannerProfile,
  shared,
} from '@tig-dpqa-cloud/contract-backend-common';
import { MarkAnonFailedBody } from './schemas/mark-anonymization-failed';
import { RequestUploadUrlResponse, uploadUrlQueryParams } from './schemas/request-upload-url';
import { z } from 'zod';
import { WSIPassport } from './schemas/wsi/wsi-passport';
import { AIHandlerResult, AIHandlerResult_v3, AIHandlerResult_v3_1_0, AIHandlerResult_v3_2_0, AIHandlerResult_v3_3_0, AIHandlerResult_v3_4_0 } from './schemas/wsi/ai-handler-result';
import { inferenceRunConfigSchema } from './schemas/wsi/inference-run-config';
import { DataplaneDLQMessage } from './schemas/dataplane-dlq-message';
import { slideScanBucketCredentials } from './schemas/credentials-response';
import { AnonymizedMetadataSchema, AnonymizedMetadataSchema_v1_0_2, ExtractedMetadataSchema, AnonymizedMetadataSchema_v1_0_3, AnonymizedMetadataSchema_v1_0_4 } from './schemas/anon-passport';
import { CloudEventsCredentialsSchema } from './schemas/cloud-events-creds';
import { anonRunConfigSchema } from './schemas/anon-run-config';
import { DataplaneHealthStatus } from './schemas/health-status-dp';
import { DPQAEndpoints } from './schemas/endpoints';
import { DeploymentCredentials, DeploymentIntegration } from './schemas/deployment';
import { OutputPassportQueryParams, OutputPassportResponse } from './schemas/output-passport';

export const authorizer: FunctionDefinition = {
  infra: { entrypointRelativeToFunctionsFolder: './authorizers/src/dataplane/index.ts', dbAccess: 'BYPASS' },
};

export const asyncFunctions = {
  asyncProcessAnonPassport: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-anon-passport-async/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  asyncSendToDataplane: {
    infra: {
      entrypointRelativeToFunctionsFolder: './events/src/send-to-dataplane-async/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  // Async functions for process passport step functions
  asyncPPSfnExtractArchive: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/extract-archive/index.ts',
      dbAccess: 'NOACCESS',
    },
  },
  asyncPPSfnStoreInfoInDatabase: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/store-in-db/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  asyncPPSfnPreAutomation: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/pre-automation/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  asyncPPSfnQualityAutomation: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/quality-automation/index.ts',
      dbAccess: 'ISOLATED',
    },
  },
  asyncPPSfnCheckAlreadyProcessed: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/check-already-processed/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  asyncPPSfnCleanupOnError: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/process-passport-sfn/clean-on-error/index.ts',
      dbAccess: 'NOACCESS',
    },
  },
  asyncAnonPrepDurable: {
    infra: {
      entrypointRelativeToFunctionsFolder: './events/src/anon-prep-durable-async/index.ts',
      dbAccess: 'BYPASS',
    },
  },
} as const satisfies Record<string, FunctionDefinition>;

export const routes = {
  getEndpoints: {
    infra: { entrypointRelativeToFunctionsFolder: './dataplanes/src/endpoints-get/index.ts', dbAccess: 'NOACCESS' },
    path: '/endpoints',
    method: 'get',
    successResponse: {
      jsonSchema: DPQAEndpoints,
      description: 'The endpoints necessary for operating SlideRightQA',
    },
  },
  getTenantConfig: {
    infra: { entrypointRelativeToFunctionsFolder: './tenants/src/config-dataplane-get/index.ts', dbAccess: 'ISOLATED_READONLY' },
    path: '/configuration',
    method: 'get',
    successResponse: {
      description: 'A request for the tenant configuration succeeded',
      jsonSchema: DataplaneSpecificConfig,
    },
  },
  getModelsConfigurationFromVersion: {
    method: 'get',
    path: '/models/configuration/{version}',
    request: {
      params: z.object({
        version: z.uuid().describe('The version of the models configuration'),
      }),
    },
    successResponse: {
      description: 'The models configuration for the current tenant',
      jsonSchema: ModelsConfigResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './models/src/models-configuration-from-version-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
  },
  getPing: { // TODO: remove after all customers are upgraded to Cedric release
    infra: { entrypointRelativeToFunctionsFolder: './dataplanes/src/ping-post/index.ts', dbAccess: 'ISOLATED' },
    path: '/ping',
    method: 'get',
  },
  postPing: {
    infra: { entrypointRelativeToFunctionsFolder: './dataplanes/src/ping-post/index.ts', dbAccess: 'ISOLATED' },
    path: '/ping',
    method: 'post',
    request: {
      body: DataplaneHealthStatus,
    },
  },
  getUploadUrl: {
    infra: { entrypointRelativeToFunctionsFolder: './slide-scans/src/upload-url-get/index.ts', dbAccess: 'ISOLATED_READONLY' },
    path: '/slide-scans/request-upload-url',
    method: 'get',
    request: {
      query: uploadUrlQueryParams,
    },
    successResponse: {
      description: 'The signed URI to upload the file.',
      jsonSchema: RequestUploadUrlResponse,
    },
  },
  putMarkAnonymizationFailed: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/mark-anon-failed-put/index.ts',
      dbAccess: 'ISOLATED',
    },
    path: '/slide-scans/{id}/mark-anonymization-failed',
    method: 'put',
    request: {
      body: MarkAnonFailedBody,
      params: z.object({
        id: shared.SlideScanId,
      }),
    },
  },
  getUploadAnonCreds: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/upload-anon-creds-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/slide-scans/{id}/upload-anon-creds',
    method: 'get',
    request: {
      params: z.object({
        id: shared.SlideScanId,
      }),
    },
    successResponse: {
      description: 'Temporary AWS credentials for uploading the anonymized package to S3.',
      jsonSchema: shared.BucketCredentialsWithKey,
    },
  } as RouteDefinition,
  getMetadataConfigCredentials: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/metadata-config-creds-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
    path: '/metadata-configuration/credentials',
    method: 'get',
    successResponse: {
      description: 'The credentials for accessing the metadata configuration',
      jsonSchema: shared.BucketCredentialsWithKey, // Key will look like <object-name>#<version-id>
    },
  },
  getSlideScansCredentials: {
    path: '/slide-scans/{id}/credentials',
    method: 'get',
    request: {
      params: z.object({
        id: shared.SlideScanId,
      }),
    },
    successResponse: {
      description: 'The credentials for the slide scan',
      jsonSchema: slideScanBucketCredentials,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/credentials-get/index.ts',
      dbAccess: 'NOACCESS',
    },
  },
  getManagementCreds: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/deployment/credentials-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/management-credentials',
    method: 'get',
    successResponse: {
      description: 'The credentials and roles that can be assumed',
      jsonSchema: shared.DataplaneManagementCredentials,
    },
  },
  getLogUploadCreds: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/log-creds-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/log-credentials',
    method: 'get',
    successResponse: {
      description: 'The credentials for uploading logs',
      jsonSchema: shared.BucketCredentialsWithKey,
    },
  },
  getCloudEventsCreds: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/cloud-events-creds-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/events-credentials',
    method: 'get',
    successResponse: {
      description: 'The queue name for the cloud events & credentials to poll said queue.',
      jsonSchema: CloudEventsCredentialsSchema,
    },
  },
  putDataplaneDLQMessage: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/dlq-message-put/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/error/dlq',
    method: 'put',
    request: {
      body: DataplaneDLQMessage,
    },
  },
  getUpgradeAvailable: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/upgrade-available-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
    path: '/upgrade/available',
    method: 'get',
    successResponse: {
      description: 'The available upgrade',
      jsonSchema: z.object({
        version: z.string().describe('The version of the available upgrade'),
      }),
    },
  },
  getDeploymentCredentials: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/deployment/credentials-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/deployment/credentials',
    method: 'get',
    successResponse: {
      description: 'The credentials and roles that can be assumed',
      jsonSchema: DeploymentCredentials,
    },
    tags: ['Deployment'],
  },
  getDeploymentIntegration: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/deployment/integration-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
    path: '/deployment/integration',
    method: 'get',
    successResponse: {
      description: 'The integration details for the deployment',
      jsonSchema: DeploymentIntegration,
    },
    tags: ['Deployment'],
  },
  getSlideScanOutputPassport: {
    infra: {
      entrypointRelativeToFunctionsFolder: './slide-scans/src/output-passport-get/index.ts',
      dbAccess: 'ISOLATED_READONLY',
    },
    path: '/slide-scans/output-passport',
    method: 'get',
    request: {
      query: OutputPassportQueryParams,
    },
    successResponse: {
      description: 'The output passport for the slide scan',
      jsonSchema: OutputPassportResponse,
    },
  },
} as const satisfies Record<string, RouteDefinition>;

export type Functions = keyof typeof routes | keyof typeof asyncFunctions | 'authorizer';

export const schemas: Record<string, z.ZodType> = {
  WSIPassport,
  AnonymizedMetadataSchema,
  AnonymizedMetadataSchema_v1_0_2,
  AnonymizedMetadataSchema_v1_0_3,
  AnonymizedMetadataSchema_v1_0_4,
  AIHandlerResult_v3,
  AIHandlerResult_v3_1_0,
  AIHandlerResult_v3_2_0,
  AIHandlerResult_v3_3_0,
  AIHandlerResult_v3_4_0,
  AIHandlerResult,
  inferenceRunConfigSchema,
  anonRunConfigSchema,
  scannerProfile,
  ExtractedMetadataSchema,
};
